import { ActionFunction, LoaderFunction } from "@remix-run/cloudflare";
import {
  useFetcher,
  useLoaderData,
  useNavigate,
  useNavigation,
  useSearchParams,
} from "@remix-run/react";
import { createClient } from "@supabase/supabase-js";
import { Database } from "~/types/supabase";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

import {
  Button,
  Card,
  CardBody,
  CardHeader,
  cn,
  Tab,
  Tabs,
  Image,
  RadioGroup,
  Radio,
} from "@heroui/react";
import { useContext, useEffect, useRef, useState } from "react";
import { getCommunityResponseWithVotesAndBookmarks } from "~/dao/CommunityResponseDAO";
import { getAuth } from "@clerk/remix/ssr.server";
import { CommunityResponseWithVotesAndBookmarks } from "~/types/communityResponse";
import {
  FaBookmark,
  FaDice,
  FaDumbbell,
  FaFlagCheckered,
  FaHeart,
  FaRegBookmark,
  FaRegHeart,
} from "react-icons/fa";
import { useUser } from "@clerk/remix";
import { AppContext } from "~/context/AppContext";
import { AppLevelModalType } from "~/types/general";
import { formatEngagementCount } from "~/utils/formatters";
import { DbScenarioGameSessionType, DbScenarioType } from "~/types/scenarios";
import { FaSkull } from "react-icons/fa6";

dayjs.extend(relativeTime);

const PAGE_SIZE = 10;

export const loader: LoaderFunction = async (args) => {
  const supabaseUrl = args.context.cloudflare.env.SUPABASE_URL;
  const supabaseKey = args.context.cloudflare.env.SUPABASE_KEY;

  if (!supabaseUrl || !supabaseKey) {
    throw new Response("Supabase credentials are missing", { status: 500 });
  }

  try {
    const supabase = createClient<Database>(supabaseUrl, supabaseKey);

    // Extract user ID from session or authentication (modify as needed)
    const { userId } = await getAuth(args);

    const url = new URL(args.request.url);
    const filter = url.searchParams.get("filter");

    // Fetch community responses with votes and bookmarks
    const responses = await getCommunityResponseWithVotesAndBookmarks(
      userId ?? "",
      supabase,
      0,
      PAGE_SIZE - 1
    );

    const supabase_builder = supabase
      .from("game_sessions")
      .select(
        `
current_turn,
max_turns,
created_at,
status,
id,
scenario:scenario_id(*),
user:user_id(user_id)
`
      )
      .not("user_id", "eq", `user_2tstRdHRThBtBFa4NpyTMr5C1UP`);

    if (filter === "succeeded") {
      supabase_builder.eq("status", "completed");
    } else if (filter === "failed") {
      supabase_builder.eq("status", "failed");
    } else {
      supabase_builder.neq("status", "active");
    }

    const { data: scenarioSessionData, error: scenarioSessionError } =
      await supabase_builder
        .order("created_at", { ascending: false })
        .range(0, PAGE_SIZE - 1);

    if (scenarioSessionError) {
      throw new Error(
        `Error fetching scenario sessions ${scenarioSessionError.message}`
      );
    }
    return {
      scenarioSessions: scenarioSessionData,
      shitTests: responses,
    };
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : "Unknown error";
    return new Response(
      JSON.stringify({
        error: "Error handling text next turn",
        message: errorMessage,
      }),
      {
        status: 500,
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  }
};

// Action function to fetch more results
export const action: ActionFunction = async (args) => {
  const supabaseUrl = args.context.cloudflare.env.SUPABASE_URL;
  const supabaseKey = args.context.cloudflare.env.SUPABASE_KEY;

  if (!supabaseUrl || !supabaseKey) {
    throw new Response("Supabase credentials are missing", { status: 500 });
  }
  const url = new URL(args.request.url);
  const filter = url.searchParams.get("filter");
  const supabase = createClient<Database>(supabaseUrl, supabaseKey);
  const formData = await args.request.formData();
  const offset = Number(formData.get("offset")) || 0;
  const selected = formData.get("selected") as string;
  const { userId } = await getAuth(args);
  if (selected !== "tests") {
    const supabase_builder = supabase
      .from("game_sessions")
      .select(
        `
current_turn,
max_turns,
created_at,
status,
id,
scenario:scenario_id(*),
user:user_id(user_id)
`
      )
      .not("user_id", "eq", `user_2tstRdHRThBtBFa4NpyTMr5C1UP`);

    if (filter === "all") {
      supabase_builder.or("status.eq.failed,status.eq.completed");
    } else if (filter === "succeeded") {
      supabase_builder.eq("status", "completed");
    } else if (filter === "failed") {
      supabase_builder.eq("status", "failed");
    }

    const { data: scenarioSessionData } = await supabase_builder
      .order("created_at", { ascending: false })
      .range(offset, offset + PAGE_SIZE - 1);

    return scenarioSessionData ?? [];
  } else {
    // Fetch community responses with votes and bookmarks
    const responses = await getCommunityResponseWithVotesAndBookmarks(
      userId ?? "",
      supabase,
      offset,
      offset + PAGE_SIZE - 1
    );
    return responses;
  }
};

export default function Community() {
  const initialData = useLoaderData<{
    scenarioSessions: (DbScenarioGameSessionType & {
      scenario: DbScenarioType;
    })[];
    shitTests: CommunityResponseWithVotesAndBookmarks[];
  }>();

  const fetcher = useFetcher();

  const navigation = useNavigation();
  const [shitTestResponses, setShitTestResponses] = useState(
    initialData.shitTests
  );
  const [scenarioSessions, setScenarioSessions] = useState<
    (DbScenarioGameSessionType & {
      scenario: DbScenarioType;
    })[]
  >(initialData.scenarioSessions);

  const [selected, setSelected] = useState<number | string>("scenarios");

  const [shitTestsOffset, setShitTestsOffset] = useState(PAGE_SIZE);
  const [scenariosOffset, setScenariosOffset] = useState(PAGE_SIZE);
  const [lastTimeLoaded, setLastTimeLoaded] = useState(Date.now());
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const filterQuery = searchParams.get("filter");
  const [filter, setFilter] = useState(filterQuery);
  const [shouldUpdate, setShouldUpdate] = useState(false);

  const loadMoreRef = useRef<HTMLDivElement>(null);
  const { isSignedIn } = useUser();

  useEffect(() => {
    if (selected !== "scenarios") {
      return;
    }
    if (shouldUpdate) {
      setScenarioSessions(initialData.scenarioSessions);
      setShouldUpdate(false);
    }
  }, [initialData, selected, shouldUpdate]);

  useEffect(() => {
    // detect url change
    setShouldUpdate(true);
    setScenariosOffset(PAGE_SIZE);
    fetcher.data = null;
  }, [window.location.href]);

  // Fetch more responses when the observer detects the bottom
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          fetcher.state === "idle" &&
          navigation.state === "idle" &&
          (selected !== "scenarios" || scenarioSessions.length > 0) &&
          Date.now() - lastTimeLoaded > 300
        ) {
          fetcher.submit(
            {
              offset:
                selected === "scenarios" ? scenariosOffset : shitTestsOffset,
              selected,
            },
            { method: "post", action: `/community?filter=${filter}` }
          );
          setLastTimeLoaded(Date.now());
        }
      },
      { threshold: 1 }
    );

    const currentRef = loadMoreRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [
    shitTestsOffset,
    fetcher,
    lastTimeLoaded,
    selected,
    scenariosOffset,
    scenarioSessions.length,
    filter,
    navigation.state,
  ]);

  useEffect(() => {
    if (fetcher.data) {
      if (selected === "scenarios") {
        setScenarioSessions((prev) => [
          ...prev,
          ...(fetcher.data as (DbScenarioGameSessionType & {
            scenario: DbScenarioType;
          })[]),
        ]);
        setScenariosOffset((prev) => prev + PAGE_SIZE);
      } else {
        setShitTestResponses((prev) => [
          ...prev,
          ...(fetcher.data as CommunityResponseWithVotesAndBookmarks[]),
        ]);
        setShitTestsOffset((prev) => prev + PAGE_SIZE);
      }
    }
  }, [fetcher.data, initialData, selected]);

  if (!initialData) {
    return <div>No data</div>;
  }

  return (
    <div className="relative p-4 pt-0 h-full min-h-screen flex flex-col justify-start items-center w-full">
      <div className="max-w-3xl mx-3 my-20 h-full w-full">
        <h1 className="font-bangers text-4xl text-center mb-2">
          Global Responses
        </h1>
        <div className="sticky top-0 gap-2 w-full flex-col justify-center flex items-center z-[1000] bg-neutral-950 py-2">
          <Tabs
            fullWidth
            size="lg"
            color="primary"
            aria-label="Options"
            className="font-bangers"
            selectedKey={selected}
            onSelectionChange={setSelected}
          >
            <Tab
              key="scenarios"
              title={
                <div className="flex items-center space-x-2">
                  <FaDice className="text-xl" />
                  <span>Scenarios</span>
                </div>
              }
            />
            <Tab
              key="tests"
              title={
                <div className="flex items-center space-x-2">
                  <FaDumbbell className="text-xl" />
                  <span>Shit Tests</span>
                </div>
              }
            />
          </Tabs>
          {selected === "scenarios" && (
            <div>
              <RadioGroup
                label=""
                orientation="horizontal"
                value={filter ?? "all"}
                onValueChange={(v) => {
                  setScenarioSessions([]);
                  setFilter(v);
                  navigate(`/community?filter=${v}`);
                }}
                className="font-bangers"
              >
                <Radio value="all">All</Radio>
                <Radio value="succeeded">Succeeded</Radio>
                <Radio value="failed">Failed</Radio>
              </RadioGroup>
            </div>
          )}
        </div>
        <div className="flex flex-col gap-4 mt-10 w-full">
          {selected === "tests" &&
            shitTestResponses.map((response, index) => (
              <CommunityResponseWithVotesCard
                key={index}
                response={response}
                isSignedIn={isSignedIn ?? false}
              />
            ))}

          {selected === "scenarios" &&
            scenarioSessions.map((session, index) => (
              <ScenariosPlayCard scenarioSession={session} key={index} />
            ))}
        </div>
        {/* Load More Button */}
        {/* @ts-expect-error legnth */}
        {((!fetcher.data || fetcher.data?.length) ?? 0) > 0 && (
          <div ref={loadMoreRef} className="h-10 mt-5 flex justify-center">
            {fetcher.state === "submitting" && <p>Loading more...</p>}
          </div>
        )}
      </div>
    </div>
  );
}

export const CommunityResponseWithVotesCard = ({
  response,
  isSignedIn,
  unmountUIDueToUnvoteCallback,
  unmountUIDueToUnbookmarkCallback,
}: {
  response: CommunityResponseWithVotesAndBookmarks;
  isSignedIn: boolean;
  unmountUIDueToUnvoteCallback?: () => void;
  unmountUIDueToUnbookmarkCallback?: () => void;
}) => {
  const [voted, setVoted] = useState(response.voted);
  const [votesCount, setVotesCount] = useState(response.votesCount);
  const [bookmarked, setBookmarked] = useState(response.bookmarked);
  const [bookmarksCount, setBookmarksCount] = useState(response.bookmarksCount);
  const { openAppLevelModal } = useContext(AppContext);

  // Toggle Vote
  const handleVote = async () => {
    try {
      if (!isSignedIn) {
        openAppLevelModal(AppLevelModalType.SignInToLikeResponse);
        return;
      }
      setVoted((prev) => !prev);
      setVotesCount((prev) => (voted ? prev - 1 : prev + 1));

      const res = await fetch("/api/community-responses/handle-vote", {
        method: "POST",
        body: new URLSearchParams({ responseId: response.id.toString() }),
      });
      if (!res.ok) {
        throw new Error("Failed to vote");
      }
      if (voted && unmountUIDueToUnvoteCallback) {
        unmountUIDueToUnvoteCallback();
      }
    } catch (error) {
      console.error("Error handling vote:", error);
      setVoted(response.voted); // Revert state on error
      setVotesCount(response.votesCount);
    }
  };

  // Toggle Favorite
  const handleBookmark = async () => {
    try {
      if (!isSignedIn) {
        openAppLevelModal(AppLevelModalType.SignInToBookmarkResponse);
        return;
      }
      setBookmarked((prev) => !prev);
      setBookmarksCount((prev) => (bookmarked ? prev - 1 : prev + 1));

      const res = await fetch("/api/community-responses/handle-bookmark", {
        method: "POST",
        body: new URLSearchParams({ responseId: response.id.toString() }),
      });
      if (!res.ok) {
        throw new Error("Failed to bookmark");
      }
      if (bookmarked && unmountUIDueToUnbookmarkCallback) {
        unmountUIDueToUnbookmarkCallback();
      }
    } catch (error) {
      console.error("Error handling bookmark:", error);
      setBookmarked(response.bookmarked); // Revert state on error
      setBookmarksCount(response.bookmarksCount);
    }
  };

  return (
    <Card className="">
      <CardBody>
        {response.created_at && (
          <p className="text-[0.6rem] opacity-70">
            {dayjs(response.created_at).fromNow()}
          </p>
        )}
        <div className="mt-3 w-full flex flex-row justify-between items-center gap-2">
          <p className="italic font-semibold opacity-90 w-full tracking-tight text-sm sm:text-base">
            {response.question}
          </p>
        </div>
        <p className="font-bold text-xl sm:text-2xl mt-2">
          {response.response}
        </p>
        <div className="flex flex-row justify-between items-center mt-4">
          <p className="font-chewy text-neutral-300 text-sm px-3 py-1 rounded-full bg-neutral-800 w-fit">
            ⏱️ {response.response_time}s
          </p>

          <div className="flex flex-row justify-center items-center gap-1">
            {/* Voting Button */}
            <Button
              size="sm"
              onPress={handleVote}
              variant={voted ? "faded" : "bordered"}
              color={voted ? "primary" : "default"}
              startContent={<>{voted ? <FaHeart /> : <FaRegHeart />}</>}
              className="hidden"
            >
              {votesCount > 0 && <>{formatEngagementCount(votesCount)}</>}
            </Button>

            <Button
              size="sm"
              onPress={handleBookmark}
              variant={bookmarked ? "faded" : "bordered"}
              color={bookmarked ? "warning" : "default"}
              startContent={
                <>{bookmarked ? <FaBookmark /> : <FaRegBookmark />}</>
              }
            >
              {bookmarksCount > 0 && (
                <>{formatEngagementCount(bookmarksCount)}</>
              )}
            </Button>
          </div>
        </div>
      </CardBody>
    </Card>
  );
};

const ScenariosPlayCard = ({
  scenarioSession,
}: {
  scenarioSession: DbScenarioGameSessionType & {
    scenario: DbScenarioType;
  };
}) => {
  const scenario = scenarioSession.scenario;
  const navigate = useNavigate();
  if (!scenario) {
    return <div>No scenario</div>;
  }
  return (
    <div className="relative font-bangers">
      <Card
        key={scenarioSession.id}
        fullWidth
        className={cn("rounded-3xl")}
        isPressable
        onPress={() => {
          navigate(
            `/scenarios/${scenarioSession.scenario.id}/${scenarioSession.id}/share?goBackTo=%2Fcommunity`
          );
        }}
      >
        <CardHeader
          className={cn(
            "absolute z-[1] flex-col h-full items-start pt-1 justify-start bg-black/40"
          )}
        >
          <h4 className="text-white font-medium text-sm drop-shadow-[2.2px_2.2px_0.1px_rgba(0,0,0,1)]">
            {scenario.name}
          </h4>

          <div className="w-full flex flex-row gap-2 justify-between items-center h-full">
            <div className="flex flex-col font-mono items-start justify-start h-full leading-tight tracking-[-0.06rem] text-xs">
              <p className="">{dayjs(scenarioSession.created_at).fromNow()}</p>
              <div className="flex items-center gap-2">
                <FaSkull />
                <p>
                  {(
                    scenarioSession.difficulty ?? scenario.difficulty
                  ).toUpperCase()}
                </p>
              </div>
              {scenario.objectives.map((objective, index) => (
                <div key={index} className="flex flex-row items-start gap-2">
                  <FaFlagCheckered />
                  <p>{objective}</p>
                </div>
              ))}
            </div>

            <p className="leading-none text-xl font-mono font-black">
              {scenarioSession.current_turn + 1} turns
            </p>
          </div>
        </CardHeader>

        <Image
          height={80}
          width={9999}
          className={cn(
            `${scenario.image_position} object-cover w-full relative z-0`,
            scenarioSession.status === "failed" && "grayscale"
          )}
          src={scenario.image_url}
          alt={scenario.name}
        />
      </Card>
    </div>
  );
};
